import React, { useMemo } from 'react';

const RouteSelector = ({ routes, selectedRoute, onChange }) => {
  // Group routes by origin for easier selection
  const groupedRoutes = useMemo(() => {
    if (!routes || routes.length === 0) return {};
    
    const grouped = {};
    routes.forEach(route => {
      if (!grouped[route.origin]) {
        grouped[route.origin] = [];
      }
      grouped[route.origin].push(route.destination);
    });
    
    return grouped;
  }, [routes]);
  
  // Get unique origins
  const origins = useMemo(() => {
    return Object.keys(groupedRoutes).sort();
  }, [groupedRoutes]);
  
  // Handle origin change
  const handleOriginChange = (e) => {
    const origin = e.target.value;
    const destination = groupedRoutes[origin]?.[0] || '';
    
    onChange({ origin, destination });
  };
  
  // Handle destination change
  const handleDestinationChange = (e) => {
    const destination = e.target.value;
    
    if (selectedRoute && selectedRoute.origin) {
      onChange({
        origin: selectedRoute.origin,
        destination
      });
    }
  };
  
  if (!routes || routes.length === 0) {
    return (
      <div className="bg-blue-50 rounded-lg p-4 text-blue-700">
        Loading available routes...
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div>
        <label 
          htmlFor="origin" 
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Origin Airport
        </label>
        <select
          id="origin"
          value={selectedRoute?.origin || ''}
          onChange={handleOriginChange}
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
        >
          <option value="" disabled>Select origin airport</option>
          {origins.map(origin => (
            <option key={origin} value={origin}>
              {origin}
            </option>
          ))}
        </select>
      </div>
      
      <div>
        <label 
          htmlFor="destination" 
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Destination Airport
        </label>
        <select
          id="destination"
          value={selectedRoute?.destination || ''}
          onChange={handleDestinationChange}
          disabled={!selectedRoute?.origin}
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
        >
          <option value="" disabled>Select destination airport</option>
          {selectedRoute?.origin && groupedRoutes[selectedRoute.origin]?.map(destination => (
            <option key={destination} value={destination}>
              {destination}
            </option>
          ))}
        </select>
      </div>
      
      <div className="pt-2">
        {selectedRoute?.origin && selectedRoute?.destination && (
          <div className="flex items-center justify-center space-x-2 bg-blue-100 text-blue-800 p-2 rounded-md">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
            </svg>
            <span className="font-medium text-lg">
              {selectedRoute.origin} to {selectedRoute.destination}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default RouteSelector;